import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { withAreaPrefix } from '../../lib/area-routes';
import { sidebarLayoutClasses } from './sidebar-layout';
import { cn } from '@/lib/utils';

interface NavItem {
  labelKey: string;
  path: string;
  icon?: string;
  requiredScope?: string;
  /** If set, user needs at least one of these scopes (takes precedence over requiredScope). */
  requiredAnyScopes?: string[];
  children?: NavItem[];
}

interface AppSidebarProps {
  readonly isCollapsed: boolean;
}

// Platform navigation items – SaaS-focused (RMT screens hidden, see FR001 Phase 0)
const platformNavItems: NavItem[] = [
  {
    labelKey: 'dashboard',
    path: '/dashboard',
    icon: '📊',
    requiredScope: 'dashboard:read',
  },
  {
    labelKey: 'products',
    path: '/products',
    icon: '🏢',
    requiredScope: 'product:read',
  },
  {
    labelKey: 'plans',
    path: '/plans',
    icon: '💳',
    requiredScope: 'plan:read',
  },
  {
    labelKey: 'projects',
    path: '/projects',
    icon: '🧩',
    requiredScope: 'project:read',
  },
  {
    labelKey: 'tenants',
    path: '/tenants',
    icon: '🏛️',
    children: [
      { labelKey: 'tenants_list', path: '/tenants', requiredScope: 'tenant:manage' },
      { labelKey: 'subscriptions', path: '/subscriptions', requiredScope: 'subscription:read' },
      { labelKey: 'tenant_users', path: '/tenant-users', requiredScope: 'tenant-user:read' },
    ],
  },
  {
    labelKey: 'settings',
    path: '/settings',
    icon: '⚙️',
    children: [
      { labelKey: 'system_users', path: '/settings/users', requiredScope: 'user:read' },
      { labelKey: 'roles', path: '/settings/roles', requiredScope: 'role:read' },
      { labelKey: 'permissions', path: '/settings/permissions', requiredScope: 'role:read' },
      { labelKey: 'project_config', path: '/settings/project-config', requiredScope: 'project:read' },
      { labelKey: 'ai_accounts', path: '/settings/service-accounts', requiredScope: 'service-account:read' },
      { labelKey: 'desktop_releases', path: '/settings/desktop-releases', requiredScope: 'desktop_release:read' },
      { labelKey: 'desktop_plugins', path: '/settings/desktop-plugins', requiredScope: 'desktop_plugin:read' },
      { labelKey: 'audit_logs', path: '/settings/audit-logs', requiredScope: 'settings:read' },
    ],
  },
];

// Tenant navigation: keep minimal set of routes that tenant users can always access.
const tenantNavItems: NavItem[] = [
  {
    labelKey: 'dashboard',
    path: '/dashboard',
    icon: '📊',
  },
  {
    labelKey: 'users',
    path: '/tenant/users',
    icon: '👥',
    requiredScope: 'tenant-user:read',
  },
  {
    labelKey: 'my_product',
    path: '/tenant/my-product',
    icon: '📦',
    requiredScope: 'product:read',
  },
  {
    labelKey: 'my_product',
    path: '/tenant/my-product',
    icon: '📦',
    requiredScope: 'plan:read',
  },
  {
    labelKey: 'projects',
    path: '/tenant/projects',
    icon: '🧩',
    requiredScope: 'project:read',
  },
];

export function AppSidebar({ isCollapsed }: AppSidebarProps) {
  const { t } = useTranslation('nav');
  const location = useLocation();
  const { hasScope, user } = useAuthStore();
  const navItems = user?.isPlatformUser ? platformNavItems : tenantNavItems;
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const toNavPath = (path: string): string => {
    if (!path.startsWith('/')) return path;
    if (path === '/unauthorized' || path.startsWith('/auth/')) return path;
    if (user?.isPlatformUser) return path.startsWith('/admin') ? path : `/admin${path}`;
    return withAreaPrefix(path, location.pathname);
  };

  // Load persisted expanded menus from localStorage
  useEffect(() => {
    const stored = globalThis.localStorage?.getItem('ffshop-expanded-menus');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as string[];
        setExpandedMenus(new Set(parsed));
      } catch {
        // Ignore invalid JSON
      }
    }
  }, []);

  // Auto-expand menu if any child is active
  useEffect(() => {
    const activePath = location.pathname;
    navItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => {
          const childPath = toNavPath(child.path);
          return activePath === childPath || activePath.startsWith(childPath + '/');
        });
        if (hasActiveChild) {
          setExpandedMenus(prev => {
            const next = new Set(prev);
            next.add(item.path);
            return next;
          });
        }
      }
    });
  }, [location.pathname, navItems]);

  const hasPermission = (item: Pick<NavItem, 'requiredScope' | 'requiredAnyScopes'>): boolean => {
    if (item.requiredAnyScopes?.length) {
      return item.requiredAnyScopes.some(s => hasScope(s));
    }
    if (!item.requiredScope) return true;
    return hasScope(item.requiredScope);
  };

  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      // Persist to localStorage
      globalThis.localStorage?.setItem('ffshop-expanded-menus', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const isMenuExpanded = (path: string): boolean => {
    return expandedMenus.has(path);
  };

  const labelFor = (labelKey: string): string => t(labelKey);
  const { sidebar: sidebarWidthClass } = sidebarLayoutClasses(isCollapsed);

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-full border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-2xl transition-all duration-300',
        sidebarWidthClass,
      )}
    >
      <div className="flex h-full flex-col">
        <div
          className={cn(
            'flex h-12 items-center border-b border-sidebar-border',
            isCollapsed ? 'justify-center px-2' : 'px-4',
          )}
        >
          {isCollapsed ? (
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-sm font-bold text-white shadow-sm"
              role="img"
              aria-label={t('brand_name')}
            >
              Y
            </div>
          ) : (
            <h1 className="min-w-0 truncate text-xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
              {t('brand_name')}
            </h1>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto no-scrollbar p-4">
          <div className="rounded-2xl space-y-1">
            <ul className="space-y-1">
              {navItems.map(item => {
                if (!hasPermission(item)) {
                  return null;
                }

                const hasChildren = item.children && item.children.length > 0;
                const visibleChildren = hasChildren
                  ? item.children!.filter(c => hasPermission(c))
                  : [];
                if (hasChildren && visibleChildren.length === 0) {
                  return null;
                }

                const itemLabel = labelFor(item.labelKey);
                const itemIsActive = hasChildren
                  ? visibleChildren.some(c => isActive(toNavPath(c.path)))
                  : isActive(toNavPath(item.path));

                return (
                  <li key={item.path}>
                    {hasChildren ? (
                      <div>
                        <button
                          type="button"
                          disabled={isCollapsed}
                          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg cursor-pointer text-left transition-colors ${
                            itemIsActive
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                              : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                          } ${isCollapsed ? 'cursor-default' : ''}`}
                          onClick={() => !isCollapsed && toggleMenu(item.path)}
                          aria-expanded={isMenuExpanded(item.path)}
                          aria-label={t('toggle_menu', { label: itemLabel })}
                        >
                          {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                          {!isCollapsed && (
                            <>
                              <span className="flex-1 min-w-0">{itemLabel}</span>
                              <span className="flex-shrink-0 transition-all duration-300 ease-in-out text-xs opacity-70">
                                {isMenuExpanded(item.path) ? '▲' : '▼'}
                              </span>
                            </>
                          )}
                        </button>
                        {!isCollapsed && (
                          <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              isMenuExpanded(item.path)
                                ? 'max-h-96 opacity-100'
                                : 'max-h-0 opacity-0'
                            }`}
                          >
                            <ul className="ml-6 mt-1 space-y-1 border-l border-sidebar-border/50 pl-2">
                              {visibleChildren.map(child => {
                                const navChildPath = toNavPath(child.path);
                                const childIsActive = isActive(navChildPath);
                                return (
                                  <li key={child.path}>
                                    <Link
                                      to={navChildPath}
                                      className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                        childIsActive
                                          ? 'bg-sidebar-accent/80 text-sidebar-accent-foreground font-medium'
                                          : 'text-sidebar-foreground/60 hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/40'
                                      }`}
                                    >
                                      {labelFor(child.labelKey)}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={toNavPath(item.path)}
                        className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg transition-colors ${
                          itemIsActive
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                        }`}
                      >
                        {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                        {!isCollapsed && <span className="min-w-0">{itemLabel}</span>}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </div>
    </aside>
  );
}
